/** The parts of Season's Home: a need's row, the alerts, the next live, Right now and the rows that open Results and Follow-ups. */
import { Link } from "react-router";
import type { AutomationSummary, HomeChain, HomeFault, HomeNeed, HomeNextLive, HomePerson, HomeResponse, HomeRightNow } from "../../../shared/types";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import { Picture } from "../../components/season/Picture";
import { SeasonCard } from "../../components/season/Card";
import { Button } from "../../components/ui/Button";
import { Face, FaceStack, Ring } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { clock, fmt, longDay, untilDay } from "../../lib/when";
import { needWords, useCopy, watchingLine } from "./parts";

const face = (p: HomePerson) => ({ name: p.name, photo: p.avatar_url });
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** One person who needs a word: their face, what they did and when. */
export function NeedRow({ need, on, onPick }: { need: HomeNeed; on: boolean; onPick: () => void }) {
  const w = needWords(need);
  return (
    <ListRow
      lead={<Face name={need.person.name} photo={need.person.avatar_url} size={44} />}
      title={need.person.name}
      sub={`${w.label}. ${w.why}`}
      clamp
      on={on}
      onClick={onPick}
      innerProps={{ "data-need": need.key }}
    />
  );
}

/** Things that need fixing before anyone is answered: emails that are off, and emails that never arrived. */
export function Alerts({ data }: { data: Pick<HomeResponse, "setup" | "emails_held" | "faults"> }) {
  const { setup, emails_held: held, faults } = data;
  if (setup.email && !faults.length) return null;
  return (
    <GroupedList className="hs-alert">
      {setup.email ? null : (
        <ListRow
          icon="mail"
          tone="brass"
          title="Your emails are off."
          sub={held ? `${fmt(held)} ${plural(held, "email", "emails")} waiting.` : "Set up email to send them."}
          to="/setup/email"
        />
      )}
      {faults.map((f: HomeFault) => (
        <ListRow
          key={f.key}
          icon="info"
          tone="brass"
          title={`${fmt(f.count)} ${plural(f.count, "email", "emails")} didn't arrive`}
          sub={`${f.automation_name}. They came back undelivered.`}
          to={`/automations/${f.automation_id}`}
        />
      ))}
    </GroupedList>
  );
}

/** The next live: its picture, when, who has registered, and the three things to do about it. */
export function NextLive({ live, invite, onInvite }: { live: HomeNextLive; invite?: "main" | "plain"; onInvite: () => void }) {
  const copy = useCopy();
  return (
    <SeasonCard flush className="hs-next">
      <Picture
        src={live.poster_url}
        size="lg"
        fallback="live"
        badge={
          <span className="pill">
            <i />
            {capital(untilDay(live.starts_at))}
          </span>
        }
      />
      <div className="hs-nextb">
        <h2>
          <Link className="hs-room" to={`/rooms/${live.webinar_id}`} data-opens={`webinar:${live.webinar_id}`}>
            {live.title}
          </Link>
        </h2>
        <p className="k-meta hs-when">{`${longDay(live.starts_at)}, ${clock(live.starts_at)} · ${live.length_min} min`}</p>
        <div className="hs-aud">
          {live.faces.length ? <FaceStack people={live.faces.slice(0, 7).map(face)} size={30} more={Math.max(0, live.registered - Math.min(7, live.faces.length))} /> : null}
          <span>
            <b>{`${fmt(live.registered)} registered`}</b>
            {` so far, ${fmt(live.since_yesterday)} since yesterday`}
          </span>
        </div>
        <div className="hs-btns">
          {invite ? (
            <Button variant={invite === "main" ? "p" : "plain"} icon="userplus" onClick={onInvite}>
              Invite people
            </Button>
          ) : null}
          <Button icon="link" onClick={() => copy(live.register_url)}>
            Copy link
          </Button>
          <Link className="btn q" to={`/rooms/${live.webinar_id}/studio`}>
            Open the studio
          </Link>
        </div>
      </div>
    </SeasonCard>
  );
}

type NowColumn = "registered" | "clicked";

/** Right now: who is watching, and how many registered and clicked the offer today. */
export function NowCard({ now, onPeople }: { now: HomeRightNow; onPeople: (which: NowColumn) => void }) {
  const { watching } = now;
  const more = watching.count - watching.people.length;
  const column = (which: NowColumn, icon: "userplus" | "pointer", label: string) => {
    const c = now[which];
    return (
      <ListRow
        icon={icon}
        title={label}
        sub={c.count ? `${fmt(c.count)} today` : "No one yet today"}
        end={c.people.length ? <FaceStack people={c.people.slice(0, 2).map(face)} size={28} /> : undefined}
        {...(c.count ? { onClick: () => onPeople(which) } : {})}
      />
    );
  };
  return (
    <SeasonCard flush label="Right now">
      <div className="hs-nowh">
        <h2>Right now</h2>
        <span className="hs-live">
          <i className="livedot" />
          Live
        </span>
        <span className="hs-time">{clock(Date.now() / 1000)}</span>
      </div>
      <div className="hs-sub">
        <Icon name="eye" /> Watching <b>{fmt(watching.count)}</b>
      </div>
      {watching.people.length ? (
        <GroupedList className="hs-flat">
          {watching.people.map((p) => (
            <ListRow
              key={p.id}
              lead={<Ring name={p.name} photo={p.avatar_url} size={40} percent={Math.round((p.minute / Math.max(1, p.of_minutes)) * 100)} live />}
              title={p.name}
              sub={watchingLine(p)}
              to={`/people/${p.id}`}
            />
          ))}
        </GroupedList>
      ) : (
        <p className="hs-quiet">No one right now</p>
      )}
      {more > 0 ? (
        <div className="hs-more">
          <FaceStack people={watching.more.map(face)} size={26} />
          <span>{`and ${fmt(more)} more${watching.more_room ? `, all in ${watching.more_room}` : ""}`}</span>
        </div>
      ) : null}
      <GroupedList className="hs-flat hs-top">
        {column("registered", "userplus", "Just registered")}
        {column("clicked", "pointer", "Clicked the offer")}
      </GroupedList>
    </SeasonCard>
  );
}

/** "38 of 1,540 became customers", or the last step the business can count when no booking app is connected. */
export function chainLine(chain: HomeChain): { words: string; end: string } {
  const last = chain.customers_connected ? chain.customers : chain.clicked;
  const share = chain.registered > 0 ? ((last / chain.registered) * 100).toFixed(1) : "0.0";
  return {
    words: `${fmt(last)} of ${fmt(chain.registered)} ${chain.customers_connected ? "became customers" : "clicked the offer"}`,
    end: `${share}% end to end`,
  };
}

/** The two rows under the cards: Your results, which opens a sheet, and Follow-ups, counted from the real automations. */
export function FootRows({ chain, autos, onResults }: { chain: HomeChain; autos: AutomationSummary[] | null; onResults: () => void }) {
  const on = autos?.filter((a) => a.status === "on").length ?? 0;
  const draft = autos?.filter((a) => a.status === "draft").length ?? 0;
  const last = chain.customers_connected ? `${fmt(chain.customers)} became customers` : `${fmt(chain.clicked)} clicked the offer`;
  return (
    <GroupedList>
      <ListRow icon="bars" title="Your results" sub={`${fmt(chain.registered)} registered, ${last}`} onClick={onResults} />
      <ListRow icon="zap" title="Follow-ups" sub={autos ? `${on} on, ${draft} draft` : undefined} to="/automations" />
    </GroupedList>
  );
}
